<template>
  <b-card bg-variant="Default">
    
    <!-- Breadcrumb per la navigazione -->
    <div class="mb-2">
      <b-breadcrumb class="breadcrumb-slash">
        <b-breadcrumb-item @click="navigaVersCartella('')" class="cursor-pointer">
          <feather-icon icon="HomeIcon" size="16" class="mr-50" />
          Home
        </b-breadcrumb-item>
        <b-breadcrumb-item 
          v-for="(folder, index) in breadcrumbItems" 
          :key="index"
          @click="navigaVersCartella(folder.path)"
          class="cursor-pointer"
          :active="index === breadcrumbItems.length - 1"
        >
          {{ folder.nome }}
        </b-breadcrumb-item>
      </b-breadcrumb>
    </div>

    <!-- Loading spinner -->
    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" label="Caricamento..."></b-spinner>
      <p class="mt-2">Caricamento in corso...</p>
    </div>

    <!-- Contenuto principale -->
    <div v-else>
      
      <!-- Sezione Cartelle -->
      <div v-if="cartelle.length > 0" class="mb-2">
        <h5 class="mb-2">
          <feather-icon icon="FolderIcon" size="18" class="mr-50" />
          Cartelle ({{ totale_cartelle }})
        </h5>
        <b-row>
          <b-col 
            xs="12" 
            md="6" 
            xl="4" 
            v-for="cartella in cartelle" 
            :key="cartella.path"
            class="mb-2"
          >
            <b-card 
              class="cursor-pointer folder-card"
              @click="navigaVersCartella(cartella.path)"
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            >
              <div class="d-flex align-items-center">
                <feather-icon 
                  icon="FolderIcon" 
                  size="40" 
                  class="text-primary mr-2"
                />
                <div>
                  <h6 class="mb-0">{{ cartella.nome }}</h6>
                  <small class="text-muted">Cartella</small>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </div>

      <!-- Sezione Files -->
      <div v-if="files.length > 0">
        <h5 class="mb-2">
          <feather-icon icon="FileIcon" size="18" class="mr-50" />
          File ({{ totale_files }})
        </h5>
        
        <vue-good-table
          :rows="files"
          :columns="columns"
          :rtl="direction"
          :pagination-options="{
            enabled: true,
            mode: 'pages',
            nextLabel: 'successiva',
            prevLabel: 'precedente',
            rowsPerPageLabel: 'righe per pagina',
            ofLabel: 'di',
            pageLabel: 'pagina',
            perPage: 10
          }"
          styleClass="vgt-table"
        >
          <div slot="emptystate"> 
            <div class="text-center"> Nessun file disponibile </div> 
          </div>

          <template
            slot="table-row"
            slot-scope="props"
          >

            <!-- Column: Nome file con icona -->
            <div
              v-if="props.column.field === 'nome'"
              class="text-nowrap"
            >
              <feather-icon 
                :icon="getFileIcon(props.row.estensione)" 
                size="18" 
                :class="getFileIconColor(props.row.estensione)"
                class="mr-1"
              />
              <span>{{ props.row.nome }}</span>
            </div>

            <!-- Column: Dimensione -->
            <span v-else-if="props.column.field === 'dimensione_leggibile'">
              {{ props.row.dimensione_leggibile }}
            </span>

            <!-- Column: Data modifica -->
            <span v-else-if="props.column.field === 'data_modifica'">
              {{ formatData(props.row.data_modifica) }}
            </span>

            <!-- Column: Download -->
            <span v-else-if="props.column.field === 'action'">
              <div class="text-nowrap">
                <a 
                  target="_blank"
                  :href="props.row.url"
                  class="text-reset"
                >
                  <feather-icon
                    :id="`file-${props.index}-download`"
                    icon="DownloadIcon"
                    class="cursor-pointer"
                    size="21"
                  />
                  <b-tooltip
                    title="Scarica file"
                    class="cursor-pointer"
                    :target="`file-${props.index}-download`"
                  />
                  Download
                </a>
              </div>
            </span>

            <!-- Column: Common -->
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>

      <!-- Nessun contenuto -->
      <div v-if="cartelle.length === 0 && files.length === 0" class="text-center py-5">
        <feather-icon icon="InboxIcon" size="48" class="text-muted mb-2" />
        <p class="text-muted">Questa cartella è vuota</p>
      </div>

    </div>

  </b-card>

</template>

<script>
import { BRow, BCol, BBreadcrumb, BBreadcrumbItem, BSpinner } from 'bootstrap-vue'
import { BCard, BTooltip } from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import store from '@/store/index'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BBreadcrumb,
    BBreadcrumbItem,
    BSpinner,
    VueGoodTable,
    BTooltip,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      isLoading: true,
      path_corrente: '',
      path_madre: '',
      cartelle: [],
      files: [],
      totale_cartelle: 0,
      totale_files: 0,
      columns: [
        {
          label: 'Nome File',
          field: 'nome',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Cerca file...',
          },
        },
        {
          label: 'Dimensione',
          field: 'dimensione_leggibile',
          sortable: true,
          width: '160px',
        },
        {
          label: 'Aggiornamento',
          field: 'data_modifica',
          sortable: true,
          width: '180px',
        },
        {
          label: 'Download',
          field: 'action',
          sortable: false,
          width: '160px',
        },
      ],
    }
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
    breadcrumbItems() {
      if (!this.path_corrente) return []
      
      const paths = this.path_corrente.split('/')
      const items = []
      let currentPath = ''
      
      paths.forEach((folder, index) => {
        currentPath = index === 0 ? folder : `${currentPath}/${folder}`
        items.push({
          nome: folder,
          path: currentPath
        })
      })
      
      return items
    },
  },
  created() {
    console.log('Componente Schede Tecniche Veicoli creato')
    this.caricaDati()
  },
  methods: {
    caricaDati() {
      this.isLoading = true
      
      // Costruisci l'URL dell'API
      let url = 'v2/schedetecnicheveicoli/esplora'
      if (this.path_corrente) {
        url += `?path=${encodeURIComponent(this.path_corrente)}`
      }
      
      console.log('Caricamento dati da:', url)
      
      this.$http.get(url)
        .then(response => { 
          console.log('Risposta API:', response.data)
          
          if (response.data.statusCode === 200 && response.data.dati && response.data.dati.length > 0) {
            const dati = response.data.dati[0]
            
            this.path_corrente = dati.path_corrente || ''
            this.path_madre = dati.path_madre || ''
            this.cartelle = dati.cartelle || []
            this.files = dati.files || []
            this.totale_cartelle = dati.totale_cartelle || 0
            this.totale_files = dati.totale_files || 0
          } else {
            // Nessun dato disponibile
            this.cartelle = []
            this.files = []
            this.totale_cartelle = 0
            this.totale_files = 0
          }
          
          this.isLoading = false
        })
        .catch(e => {
          console.error('Errore nel caricamento dei dati:', e)
          this.isLoading = false
          
          // Reset dati in caso di errore
          this.cartelle = []
          this.files = []
          this.totale_cartelle = 0
          this.totale_files = 0
        })
    },
    
    navigaVersCartella(path) {
      this.path_corrente = path
      this.caricaDati()
    },
    
    getFileIcon(estensione) {
      const ext = estensione.toLowerCase()
      const iconMap = {
        pdf: 'FileTextIcon',
        doc: 'FileTextIcon',
        docx: 'FileTextIcon',
        xls: 'FileIcon',
        xlsx: 'FileIcon',
        jpg: 'ImageIcon',
        jpeg: 'ImageIcon',
        png: 'ImageIcon',
        gif: 'ImageIcon',
        zip: 'ArchiveIcon',
        rar: 'ArchiveIcon',
        txt: 'FileTextIcon',
        csv: 'FileTextIcon',
      }
      
      return iconMap[ext] || 'FileIcon'
    },
    
    getFileIconColor(estensione) {
      const ext = estensione.toLowerCase()
      const colorMap = {
        pdf: 'text-danger',
        doc: 'text-primary',
        docx: 'text-primary',
        xls: 'text-success',
        xlsx: 'text-success',
        jpg: 'text-warning',
        jpeg: 'text-warning',
        png: 'text-warning',
        gif: 'text-warning',
        zip: 'text-secondary',
        rar: 'text-secondary',
        txt: 'text-info',
        csv: 'text-info',
      }
      
      return colorMap[ext] || 'text-muted'
    },
    
    formatData(dataString) {
      if (!dataString) return ''
      
      // Formato input: "2017-02-28 14:16:35"
      // Formato output: "gg/mm/YYYY hh:ii"
      const data = new Date(dataString)
      
      const giorno = String(data.getDate()).padStart(2, '0')
      const mese = String(data.getMonth() + 1).padStart(2, '0')
      const anno = data.getFullYear()
      const ore = String(data.getHours()).padStart(2, '0')
      const minuti = String(data.getMinutes()).padStart(2, '0')
      
      return `${giorno}/${mese}/${anno} ${ore}:${minuti}`
    },
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>

<style lang="scss" scoped>
.folder-card {
  transition: all 0.2s ease-in-out;
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.1);
    border-color: rgba(113, 102, 240, 0.3);
  }
}

.cursor-pointer {
  cursor: pointer;
}

.breadcrumb-slash {
  .breadcrumb-item + .breadcrumb-item::before {
    content: '/';
  }
}
</style>
